import React, { useState, useEffect } from "react";
import { FaPlus, FaTimes, FaPen } from "react-icons/fa";
import { useCurrency } from "../../../../context/currencyContext";

const API_URL = "https://hms.backend.hiragroupcloud.com/api/cases";

const DraftInvoice = ({ show, onHide, caseId, caseNumber }) => {
  const { formatAmount } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [caseData, setCaseData] = useState(null);
  const [units, setUnits] = useState({});
  const [prices, setPrices] = useState({}); // Now prices are per unit
  const [additionalLines, setAdditionalLines] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [isTaxable, setIsTaxable] = useState(false);
  const [subtotal, setSubtotal] = useState(0);

  // ---------- FETCH CASE DATA ----------
  useEffect(() => {
    if (!show || !caseId) return;
    const fetchCase = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_URL}/cases/${caseId}`);
        if (!res.ok) throw new Error("Case not found");
        const data = await res.json();
        setCaseData(data);
        // Initialize units and per-unit prices from procedure data
        const initialUnits = {};
        const initialPrices = {};
        if (data.procedures?.length) {
          data.procedures.forEach((proc, i) => {
            const calcUnits = proc.calculatedUnits || 1;
            initialUnits[i] = calcUnits;
            initialPrices[i] = (proc.price || 0) / calcUnits;
          });
        }
        setUnits(initialUnits);
        setPrices(initialPrices);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCase();
  }, [show, caseId]);

  // ---------- CALCULATE TOTAL ----------
  useEffect(() => {
    const totalProcedures = Object.entries(units).reduce((sum, [i, unitCount]) => {
      const pricePerUnit = prices[i] || 0;
      return sum + (unitCount * pricePerUnit);
    }, 0);

    const addTotal = additionalLines.reduce((sum, l) => sum + (l.amount || 0), 0);
    setSubtotal(totalProcedures + addTotal);
  }, [units, prices, additionalLines]);

  // ---------- UNIT HANDLERS ----------
  const handleUnitChange = (index, value) => {
    const unitValue = Math.max(1, Number(value) || 1); // Minimum 1 unit
    setUnits(prev => ({ ...prev, [index]: unitValue }));
  };

  // ---------- PRICE HANDLERS ----------
  const handlePriceChange = (index, value) => {
    const priceValue = Math.max(0, Number(value) || 0); // Minimum 0 price
    setPrices(prev => ({ ...prev, [index]: priceValue }));
  };

  // ---------- ADDITIONAL LINE HANDLERS ----------
  const handleAddLine = () => {
    const newLine = { id: Date.now(), amount: 0, taxable: false };
    setAdditionalLines([...additionalLines, newLine]);
  };

  const handleDeleteLine = (id) =>
    setAdditionalLines(additionalLines.filter((l) => l.id !== id));

  const handleEditLine = (line) => {
    setEditingLine(line);
    setIsTaxable(line.taxable || false);
    setShowModal(true);
  };

  const handleSaveModal = () => {
    if (editingLine) {
      setAdditionalLines(
        additionalLines.map((l) =>
          l.id === editingLine.id ? { ...l, taxable: isTaxable } : l
        )
      );
    }
    setShowModal(false);
    setEditingLine(null);
  };

  const handleLineAmountChange = (id, value) => {
    setAdditionalLines((prev) =>
      prev.map((l) => (l.id === id ? { ...l, amount: Number(value) || 0 } : l))
    );
  };

  // ---------- CALCULATE AMOUNT FOR EACH PROCEDURE ----------
  const calculateProcedureAmount = (index) => {
    const unitCount = units[index] || 1;
    const pricePerUnit = prices[index] || 0;
    return unitCount * pricePerUnit;
  };

  // ---------- SAVE DRAFT (UPDATED) ----------
  const handleSaveDraft = async () => {
    // Prepare updated procedures with total price per procedure
    const updatedProcedures = caseData?.procedures?.map((proc, i) => {
      const newUnits = units[i] || proc.calculatedUnits || 1;
      const newPricePerUnit = prices[i] || (proc.price / (proc.calculatedUnits || 1)) || 0;
      const newTotalPrice = newUnits * newPricePerUnit;
      return {
        ...proc,
        calculatedUnits: newUnits,
        price: newTotalPrice, // Store total price as per model
      };
    }) || [];

    // Calculate totalProcedures (sum of procedure totals only)
    const totalProcedures = updatedProcedures.reduce((sum, proc) => sum + (proc.price || 0), 0);
    const invoiceData = {
      caseId,
      caseNumber,
      procedures: updatedProcedures,
      units,
      prices, // per unit
      additionalLines,
      subtotal,
    };

    // Save to localStorage
    localStorage.setItem("invoiceData", JSON.stringify(invoiceData));

    try {
      // Save updated total and procedures to backend (totalAmount is only for procedures)
      const response = await fetch(`${API_URL}/${caseId}/total-amount`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ totalAmount: totalProcedures, procedures: updatedProcedures }),
      });
      if (!response.ok) {
        throw new Error('Failed to update case in database');
      }
      const result = await response.json();

      alert("Invoice Draft Saved Successfully! Case updated with new units, prices, and total amount.");
      onHide();
    } catch (error) {
      console.error('Error updating case:', error);
      alert('Invoice draft saved locally but failed to update case in database.');
    }
  };

  const boxStyle = {
    width: "70px",
    height: "36px",
    textAlign: "center",
    border: "1px solid #ccc",
    borderRadius: "6px",
    padding: "4px",
    fontSize: "14px",
  };

  const descriptionStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "1.3",
    textAlign: "center",
  };

  const descriptionHeading = { fontWeight: "bold", marginBottom: "8px" };
  const smallText = { fontSize: "13px", color: "#555", marginTop: "8px" };

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1050,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          width: "850px", // Increased width to accommodate extra column
          maxHeight: "90vh",
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          overflow: "hidden",
          fontFamily: "Arial, sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "#004aad",
            color: "white",
            padding: "15px 20px",
            fontWeight: "bold",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Invoice Draft - Case #{caseNumber}</span>
          <span
            onClick={onHide}
            style={{ cursor: "pointer", fontSize: "18px", fontWeight: "bold" }}
          >
            ✕
          </span>
        </div>

        <div style={{ padding: "15px", overflowY: "auto", maxHeight: "70vh" }}>
          {loading && <p style={{ textAlign: "center" }}>Loading case…</p>}
          {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}

          <h3
            style={{
              color: "#333",
              fontSize: "18px",
              marginBottom: "10px",
              borderBottom: "2px solid #004aad",
              paddingBottom: "5px",
              textAlign: "left",
            }}
          >
            Invoice Details
          </h3>

          {/* HEADER */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 3fr 1fr 1fr 1fr",
              backgroundColor: "#f6f6f6",
              border: "1px solid #ccc",
              fontWeight: "bold",
              padding: "10px",
              textAlign: "center",
            }}
          >
            <div>Units</div>
            <div>Description</div>
            <div>Price/Unit</div>
            <div>Amount</div>
          </div>

          {/* PROCEDURE ROWS */}
          {caseData?.procedures?.map((proc, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 3fr 1fr 1fr 1fr",
                borderBottom: "1px solid #ddd",
                textAlign: "center",
                padding: "20px 0",
                alignItems: "center",
              }}
            >
              {/* Units Input */}
              <div>
                <input
                  type="number"
                  value={units[i] || proc.calculatedUnits || 1}
                  min={1}
                  onChange={(e) => handleUnitChange(i, e.target.value)}
                  style={boxStyle}
                />
              </div>

              {/* Description */}
              <div style={descriptionStyle}>
                <div style={descriptionHeading}>{proc.procedure}</div>
                <div style={smallText}>
                  {proc.teeth && <div>Teeth: {proc.teeth}</div>}
                  {proc.shade && <div>Shade: {proc.shade}</div>}
                </div>
              </div>

              {/* Price Input */}
              <div>
                <input
                  type="number"
                  value={prices[i] ?? (proc.price / (proc.calculatedUnits || 1)) ?? 0}
                  min={0}
                  step="0.01"
                  onChange={(e) => handlePriceChange(i, e.target.value)}
                  style={boxStyle}
                />
              </div>

              {/* Calculated Amount */}
              <div>
                <input
                  type="number"
                  value={calculateProcedureAmount(i)}
                  readOnly
                  style={{...boxStyle, backgroundColor: "#f9f9f9"}}
                />
              </div>
            </div>
          ))}

          {/* ADDITIONAL LINES */}
          <div
            style={{
              borderBottom: "1px solid #ccc",
              padding: "12px 0",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              marginTop: "20px",
            }}
          >
            <span style={{ fontWeight: "bold" }}>Additional Lines</span>
            <button
              onClick={handleAddLine}
              style={{
                backgroundColor: "#004aad",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                fontSize: "18px",
                cursor: "pointer",
              }}
            >
              <FaPlus />
            </button>
          </div>

          {additionalLines.map((line) => (
            <div
              key={line.id}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                padding: "10px 0",
                borderBottom: "1px dashed #eee",
              }}
            >
              <input
                type="number"
                value={line.amount}
                onChange={(e) => handleLineAmountChange(line.id, e.target.value)}
                style={{ ...boxStyle, width: "90px" }}
                placeholder="Amount"
              />
              <button
                onClick={() => handleEditLine(line)}
                style={{
                  backgroundColor: "#004aad",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                }}
              >
                <FaPen style={{ fontSize: "12px" }} />
              </button>
              <button
                onClick={() => handleDeleteLine(line.id)}
                style={{
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                }}
              >
                <FaTimes style={{ fontSize: "12px" }} />
              </button>
            </div>
          ))}

          {/* TOTALS */}
          <div style={{ marginTop: "20px", borderTop: "2px solid #004aad", paddingTop: "15px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 15px",
                borderBottom: "1px solid #ddd",
              }}
            >
              <span>Subtotal</span>
              <span style={{ fontWeight: "bold" }}>{formatAmount(subtotal)}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 15px",
                borderBottom: "1px solid #ddd",
              }}
            >
              <span>Taxes</span>
              <span style={{ fontWeight: "bold" }}>{formatAmount(0)}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 15px",
                fontWeight: "bold",
                fontSize: "16px",
                backgroundColor: "#f6f6f6",
                borderRadius: "4px",
              }}
            >
              <span>Total</span>
              <span>{formatAmount(subtotal)}</span>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "15px 20px",
            backgroundColor: "#f8f9fa",
            gap: "10px",
            borderTop: "1px solid #eee",
          }}
        >
          <button
            onClick={onHide}
            style={{
              backgroundColor: "#E0E0E0",
              color: "#333",
              border: "none",
              borderRadius: "4px",
              padding: "10px 18px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveDraft}
            style={{
              backgroundColor: "#004aad",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "10px 18px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Save Draft
          </button>
        </div>
      </div>

      {/* TAX MODAL */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              width: "360px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                backgroundColor: "#004aad",
                color: "#fff",
                padding: "12px 16px",
                fontWeight: "bold",
                fontSize: "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Manage Line Item Tax</span>
              <span
                onClick={() => setShowModal(false)}
                style={{ cursor: "pointer", fontSize: "20px", fontWeight: "bold" }}
              >
                ×
              </span>
            </div>
            <div style={{ padding: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                Is Line Item Taxable?
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="checkbox"
                  checked={isTaxable}
                  onChange={(e) => setIsTaxable(e.target.checked)}
                  style={{ width: "18px", height: "18px", accentColor: "#004aad" }}
                />
                <span style={{ fontSize: "14px" }}>Yes, this line item is taxable</span>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                padding: "0 20px 20px",
              }}
            >
              <button
                onClick={() => setShowModal(false)}
                style={{
                  backgroundColor: "#ccc",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 16px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveModal}
                style={{
                  backgroundColor: "#004aad",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  padding: "8px 16px",
                  cursor: "pointer",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftInvoice;